import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { applyActionCode, checkActionCode } from 'firebase/auth';
import { auth } from '../firebase';
import './AuthAction.css';

// Handles Firebase's email-verification and email-change-recovery links
// (?mode=...&oobCode=...). Password reset doesn't apply here -- this app is
// email-link (passwordless) sign-in only, which completes itself in
// SignPage.js and never routes through this page.
const AuthAction = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');

  const runVerifyEmail = useCallback(async () => {
    try {
      await applyActionCode(auth, oobCode);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message || 'This link is invalid or has expired.');
    }
  }, [oobCode]);

  const runRecoverEmail = useCallback(async () => {
    try {
      await checkActionCode(auth, oobCode);
      await applyActionCode(auth, oobCode);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message || 'This link is invalid or has expired.');
    }
  }, [oobCode]);

  useEffect(() => {
    if (!oobCode || (mode !== 'verifyEmail' && mode !== 'recoverEmail')) {
      setStatus('error');
      setError('This link is invalid or not supported.');
      return;
    }

    if (mode === 'verifyEmail') {
      runVerifyEmail();
    } else {
      runRecoverEmail();
    }
  }, [mode, oobCode, runVerifyEmail, runRecoverEmail]);

  const renderBody = () => {
    if (status === 'verifying') {
      return <p className="auth-action-subtitle">Verifying your link...</p>;
    }

    if (status === 'error') {
      return (
        <>
          <p className="auth-action-subtitle auth-action-error">{error}</p>
          <Link className="auth-action-button" to="/signup">
            Back to Sign In
          </Link>
        </>
      );
    }

    const messages = {
      verifyEmail: 'Your email address has been verified.',
      recoverEmail: 'Your account email has been restored.',
    };
    return (
      <>
        <p className="auth-action-subtitle">{messages[mode] || 'All set!'}</p>
        <button
          className="auth-action-button"
          onClick={() => navigate(continueUrl || '/signup')}
        >
          Continue to Sign In
        </button>
      </>
    );
  };

  return (
    <div className="auth-action-container">
      <div className="auth-action-card">
        <h1 className="auth-action-header">getSatSat</h1>
        {renderBody()}
      </div>
    </div>
  );
};

export default AuthAction;
